import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Save,
  Calendar,
  Clock,
  AlertCircle,
  User,
  Mail,
  MapPin,
  Palette,
  Menu,
  Settings,
  LogOut,
  Briefcase,
  Image,
  UserPlus,
  Copy,
  Check,
  ExternalLink,
  ToggleLeft,
  ToggleRight,
  Shield,
  Receipt,
  Share2,
  Globe
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { SocialConnectionsPanel } from '../components/SocialConnectionsPanel';
import { KoppelingenSection } from '../components/KoppelingenSection';
import { calculateEffectiveEndDate, formatDateNL } from '../lib/billingDates';
import { CancelSubscriptionModal } from '../components/CancelSubscriptionModal';
import { BlogSettingsSection, BlogSettingsHandle } from '../components/BlogSettingsSection';
import { CreditBalance } from '../components/CreditBalance';
import { CreditHistory } from '../components/CreditHistory';
import { MaintenanceBanner } from '../components/MaintenanceBanner';
import { WhatsAppSection } from '../components/WhatsAppSection';
import { LowCreditsBanner } from '../components/LowCreditsBanner';
import { Sidebar } from '../components/Sidebar';
import { MobileNav } from '../components/MobileNav';
import { PageHeader } from '../components/design-system/PageHeader';
import { ToastContainer, ToastType } from '../components/Toast';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';

const DAYS_OF_WEEK = [
  { value: 1, label: 'Maandag' },
  { value: 2, label: 'Dinsdag' },
  { value: 3, label: 'Woensdag' },
  { value: 4, label: 'Donderdag' },
  { value: 5, label: 'Vrijdag' },
  { value: 6, label: 'Zaterdag' },
  { value: 0, label: 'Zondag' },
];

type TabType = 'profile' | 'email' | 'blog' | 'business_profile' | 'image_style' | 'socials' | 'wordpress' | 'subscription' | 'billing';

const VALID_TABS: TabType[] = ['profile', 'email', 'blog', 'business_profile', 'image_style', 'socials', 'wordpress', 'subscription', 'billing'];

const SETTINGS_MENU: { key: TabType; label: string; icon: typeof User }[] = [
  { key: 'profile', label: 'Gegevens', icon: User },
  { key: 'email', label: 'Email', icon: Mail },
  { key: 'blog', label: 'Blog', icon: Palette },
  { key: 'business_profile', label: 'Bedrijfsprofiel', icon: Briefcase },
  { key: 'image_style', label: 'Afbeeldingsstijl', icon: Image },
  { key: 'socials', label: 'Socials', icon: Share2 },
  { key: 'wordpress', label: 'Koppeling', icon: Globe },
  { key: 'subscription', label: 'Inschrijfformulier', icon: UserPlus },
  { key: 'billing', label: 'Facturen & abonnement', icon: Receipt },
];

const PLUGANDPAY_PORTAL_URL = 'https://portal.plugandpay.com/login/dibbi-37768';

export function DashboardSettings() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, refreshUser } = useAuth();
  const blogSettingsRef = useRef<BlogSettingsHandle>(null);
  const [saving, setSaving] = useState(false);
  const [toasts, setToasts] = useState<Array<{ id: string; type: ToastType; title: string; message?: string; duration?: number }>>([]);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const rawTab = searchParams.get('tab') as TabType | null;
  const activeTab: TabType = rawTab && VALID_TABS.includes(rawTab) ? rawTab : 'profile';

  const setActiveTab = (tab: TabType) => {
    setSearchParams({ tab }, { replace: true });
  };

  const addToast = (type: 'success' | 'error' | 'info', message: string) => {
    const id = Date.now().toString();
    setToasts(prev => [...prev, { id, type, title: message }]);
  };

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  const [profileData, setProfileData] = useState({
    first_name: '',
    company_name: '',
    website_url: '',
    address: '',
    city: '',
    postal_code: '',
    country: 'Nederland',
    email_greeting_style: 'Beste {{first_name|lezer}}',
    email_closing_style: 'Met vriendelijke groet,\n{{first_name}}\n{{company_name}}',
  });

  const [scheduleData, setScheduleData] = useState({
    blog_schedule_enabled: false,
    blog_schedule_days_of_week: [] as number[],
    blog_schedule_time: '08:00',
    blog_schedule_timezone: 'Europe/Amsterdam',
  });

  const [businessProfile, setBusinessProfile] = useState('');
  const [imageStyle, setImageStyle] = useState('');

  const [subscriptionForm, setSubscriptionForm] = useState({
    is_active: true,
    title: 'Schrijf je in voor mijn nieuwsbrief',
    description: 'Blijf op de hoogte van de laatste blogs en updates.',
    button_text: 'Inschrijven',
    success_title: 'Bedankt voor je inschrijving!',
    success_message: 'Controleer je inbox voor een bevestigingsemail.',
    confirmation_required: true,
    confirmation_subject: 'Bevestig je inschrijving',
    confirmation_message: 'Klik op de knop hieronder om je inschrijving te bevestigen.',
    gdpr_enabled: false,
    gdpr_text: 'Ik ga akkoord met de verwerking van mijn gegevens voor het ontvangen van de nieuwsbrief.',
    gdpr_privacy_url: '',
  });
  const [subscriptionFormId, setSubscriptionFormId] = useState<string | null>(null);
  const [subscriptionFormLoaded, setSubscriptionFormLoaded] = useState(false);
  const [savingSubscription, setSavingSubscription] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  const [activeCancellation, setActiveCancellation] = useState<{
    id: string;
    cancelled_at: string;
    effective_end_date: string;
  } | null>(null);
  const [cancellationLoaded, setCancellationLoaded] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);

  const greetingEditor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false,
      }),
      Underline,
    ],
    content: profileData.email_greeting_style,
    onUpdate: ({ editor }) => {
      setProfileData(prev => ({ ...prev, email_greeting_style: editor.getHTML() }));
    },
  });

  const closingEditor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false,
      }),
      Underline,
    ],
    content: profileData.email_closing_style,
    onUpdate: ({ editor }) => {
      setProfileData(prev => ({ ...prev, email_closing_style: editor.getHTML() }));
    },
  });

  const loadSubscriptionForm = async () => {
    if (!user) return;
    const { data } = await supabase
      .from('subscription_forms')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle();
    if (data) {
      setSubscriptionFormId(data.id);
      setSubscriptionForm({
        is_active: data.is_active,
        title: data.title,
        description: data.description,
        button_text: data.button_text,
        success_title: data.success_title,
        success_message: data.success_message,
        confirmation_required: data.confirmation_required,
        confirmation_subject: data.confirmation_subject,
        confirmation_message: data.confirmation_message,
        gdpr_enabled: data.gdpr_enabled ?? false,
        gdpr_text: data.gdpr_text ?? 'Ik ga akkoord met de verwerking van mijn gegevens voor het ontvangen van de nieuwsbrief.',
        gdpr_privacy_url: data.gdpr_privacy_url ?? '',
      });
    }
    setSubscriptionFormLoaded(true);
  };

  const handleSaveSubscriptionForm = async () => {
    if (!user) return;
    try {
      setSavingSubscription(true);
      if (subscriptionFormId) {
        const { error } = await supabase
          .from('subscription_forms')
          .update({ ...subscriptionForm, updated_at: new Date().toISOString() })
          .eq('id', subscriptionFormId);
        if (error) throw error;
      } else {
        const { data, error } = await supabase
          .from('subscription_forms')
          .insert({ user_id: user.id, ...subscriptionForm })
          .select('id')
          .single();
        if (error) throw error;
        setSubscriptionFormId(data.id);
      }
      addToast('success', 'Inschrijfformulier opgeslagen!');
    } catch (err: any) {
      addToast('error', err.message || 'Kon inschrijfformulier niet opslaan');
    } finally {
      setSavingSubscription(false);
    }
  };

  const handleCopySubscribeLink = () => {
    if (!user?.username_slug) return;
    const link = `https://lees-nu.nl/inschrijven/${user.username_slug}`;
    navigator.clipboard.writeText(link);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  useEffect(() => {
    if (user) {
      const greetingStyle = user.email_greeting_style || '<p>Beste {{first_name|lezer}}</p>';
      const closingStyle = user.email_closing_style || '<p>Met vriendelijke groet,<br>{{first_name}}<br>{{company_name}}</p>';

      setProfileData({
        first_name: user.first_name,
        company_name: user.company_name,
        website_url: user.website_url || '',
        address: user.address || '',
        city: user.city || '',
        postal_code: user.postal_code || '',
        country: user.country || 'Nederland',
        email_greeting_style: greetingStyle,
        email_closing_style: closingStyle,
      });

      if (greetingEditor && greetingEditor.getHTML() !== greetingStyle) {
        greetingEditor.commands.setContent(greetingStyle);
      }

      if (closingEditor && closingEditor.getHTML() !== closingStyle) {
        closingEditor.commands.setContent(closingStyle);
      }

      setScheduleData({
        blog_schedule_enabled: user.blog_schedule_enabled || false,
        blog_schedule_days_of_week: user.blog_schedule_days_of_week || [],
        blog_schedule_time: user.blog_schedule_time || '08:00',
        blog_schedule_timezone: user.blog_schedule_timezone || 'Europe/Amsterdam',
      });

      setBusinessProfile(user.user_profile || '');
      setImageStyle(user.image_preferences || '');
    }
  }, [user, greetingEditor, closingEditor]);

  const calculateNextScheduledDate = () => {
    if (!scheduleData.blog_schedule_enabled || scheduleData.blog_schedule_days_of_week.length === 0) return null;

    const now = new Date();
    const [hours, minutes] = scheduleData.blog_schedule_time.split(':').map(Number);
    const currentDay = now.getDay();
    const currentTime = now.getHours() * 60 + now.getMinutes();
    const scheduledTime = hours * 60 + minutes;

    const sortedDays = [...scheduleData.blog_schedule_days_of_week].sort((a, b) => a - b);
    let daysUntilNext: number | null = null;

    if (sortedDays.includes(currentDay) && currentTime < scheduledTime) {
      daysUntilNext = 0;
    } else {
      for (const day of sortedDays) {
        if (day > currentDay) {
          daysUntilNext = day - currentDay;
          break;
        }
      }

      if (daysUntilNext === null) {
        daysUntilNext = (7 - currentDay) + sortedDays[0];
      }
    }

    const next = new Date(now);
    next.setDate(next.getDate() + daysUntilNext);
    next.setHours(hours, minutes, 0, 0);

    return next;
  };

  const handleProfileChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setProfileData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleScheduleChange = (field: string, value: any) => {
    setScheduleData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDayToggle = (dayValue: number) => {
    setScheduleData(prev => {
      const currentDays = prev.blog_schedule_days_of_week;
      const newDays = currentDays.includes(dayValue)
        ? currentDays.filter(d => d !== dayValue)
        : [...currentDays, dayValue];

      return {
        ...prev,
        blog_schedule_days_of_week: newDays
      };
    });
  };

  const handleSave = async () => {
    if (!user) return;

    try {
      setSaving(true);

      const nextScheduled = calculateNextScheduledDate();

      const updates: any = {
        ...profileData,
        ...scheduleData,
        blog_schedule_frequency: 'weekly',
        next_blog_scheduled_at: nextScheduled?.toISOString() || null,
        user_profile: businessProfile,
        image_preferences: imageStyle,
        updated_at: new Date().toISOString(),
      };

      const { error: updateError } = await supabase
        .from('users')
        .update(updates)
        .eq('id', user.id);

      if (updateError) throw updateError;

      if (blogSettingsRef.current) {
        try {
          await blogSettingsRef.current.saveBlogSettings();
        } catch (blogSettingsError: any) {
          console.error('Blog settings save failed:', blogSettingsError);
          throw new Error(`Blog instellingen konden niet worden opgeslagen: ${blogSettingsError.message}`);
        }
      }

      await refreshUser();
      addToast('success', 'Instellingen succesvol opgeslagen!');
    } catch (err: any) {
      console.error('Error saving settings:', err);
      addToast('error', err.message || 'Er ging iets mis bij het opslaan');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'subscription' && !subscriptionFormLoaded && user) {
      loadSubscriptionForm();
    }
  }, [activeTab, user]);

  useEffect(() => {
    if (activeTab === 'billing' && !cancellationLoaded && user) {
      loadActiveCancellation();
    }
  }, [activeTab, user]);

  const loadActiveCancellation = async () => {
    if (!user) return;
    const { data } = await supabase
      .from('subscription_cancellations')
      .select('id, cancelled_at, effective_end_date')
      .eq('user_id', user.id)
      .is('reversed_at', null)
      .maybeSingle();
    setActiveCancellation(data ?? null);
    setCancellationLoaded(true);
  };

  const nextScheduledDate = calculateNextScheduledDate();

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#F2F2EF]">
      <Sidebar onSignOut={handleSignOut} />
      <MobileNav
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        onSignOut={handleSignOut}
      />
      <div className="lg:pl-64">
        <MaintenanceBanner />
        <LowCreditsBanner />
        <header className="bg-white border-b border-gray-200 sticky top-0 z-40 lg:hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setIsMobileMenuOpen(true)}
                  className="lg:hidden p-2 text-gray-700 hover:text-[#FF6B35] hover:bg-gray-100 rounded-lg transition-colors"
                  title="Menu"
                >
                  <Menu className="w-6 h-6" />
                </button>
                <img
                  src="https://cdn.prod.website-files.com/68106eab8acafbe1be69f17a/6830b7212ae8f51f0b5e545b_12ebc92fd52362293d48e2636ef26ed3_logo.svg"
                  alt="Dibbi Logo"
                  className="h-8 lg:hidden"
                />
              </div>
              <div className="flex items-center gap-3 lg:hidden">
                <button
                  onClick={() => navigate('/dashboard/settings')}
                  className="p-2 text-gray-700 hover:text-[#FF6B35] hover:bg-gray-100 rounded-lg transition-colors"
                  title="Instellingen"
                >
                  <Settings className="w-6 h-6" />
                </button>
                <button
                  onClick={handleSignOut}
                  className="p-2 text-gray-700 hover:text-[#FF6B35] hover:bg-gray-100 rounded-lg transition-colors"
                  title="Uitloggen"
                >
                  <LogOut className="w-6 h-6" />
                </button>
              </div>
            </div>
          </div>
        </header>
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8 lg:pt-8">
          <div className="space-y-6">
            <PageHeader
              title="Instellingen"
              subtitle="Beheer je profiel, voorkeuren, abonnement en facturen"
            />

            <div className="flex flex-col lg:flex-row lg:gap-8">
              <nav className="lg:w-56 flex-shrink-0 mb-6 lg:mb-0">
                <div className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0">
                  {SETTINGS_MENU.map(({ key, label, icon: Icon }) => (
                    <button
                      key={key}
                      onClick={() => setActiveTab(key)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors lg:w-full ${
                        activeTab === key
                          ? 'bg-[#FF6B35]/10 text-[#FF6B35]'
                          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                      }`}
                    >
                      <Icon className="w-4 h-4 flex-shrink-0" />
                      {label}
                    </button>
                  ))}
                </div>
              </nav>

              <div className="flex-1 min-w-0 space-y-6">

            {activeTab === 'profile' && (
              <div className="space-y-6">
                {user?.id && (
                  <CreditBalance userId={user.id} />
                )}

                {user?.id && (
                  <CreditHistory userId={user.id} />
                )}

                <div className="bg-white rounded-2xl border border-gray-200 p-6">
                  <h2 className="text-lg font-bold text-[#231F20] mb-6 flex items-center gap-2">
                    <User className="w-5 h-5" />
                    Bedrijfs gegevens
                  </h2>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Voornaam
                      </label>
                      <input
                        type="text"
                        name="first_name"
                        value={profileData.first_name}
                        onChange={handleProfileChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Bedrijfsnaam
                      </label>
                      <input
                        type="text"
                        name="company_name"
                        value={profileData.company_name}
                        onChange={handleProfileChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Website URL
                      </label>
                      <input
                        type="url"
                        name="website_url"
                        value={profileData.website_url}
                        onChange={handleProfileChange}
                        placeholder="https://www.voorbeeld.nl"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent outline-none"
                      />
                    </div>
                  </div>
                </div>

                {user?.id && (
                  <div className="bg-white rounded-2xl border border-gray-200 p-6">
                    <WhatsAppSection
                      userId={user.id}
                      initialPhone={user.whatsapp_phone ?? null}
                      initialVerifiedAt={user.whatsapp_verified_at ?? null}
                    />
                  </div>
                )}
              </div>
            )}

            {activeTab === 'email' && (
              <div className="space-y-6">
                <div className="bg-white rounded-2xl border border-gray-200 p-6">
                  <h2 className="text-lg font-bold text-[#231F20] mb-2 flex items-center gap-2">
                    <Mail className="w-5 h-5" />
                    Email voorkeuren
                  </h2>
                  <p className="text-sm text-gray-600 mb-6">
                    Stel je standaard aanhef en afsluiting in voor email campaigns
                  </p>

                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Aanhef template
                      </label>
                      <div className="border border-gray-300 rounded-lg overflow-hidden">
                        <div className="bg-gray-50 border-b border-gray-300 p-2 flex gap-1">
                          <button
                            onClick={() => greetingEditor?.chain().focus().toggleBold().run()}
                            className={`p-2 rounded hover:bg-gray-200 ${greetingEditor?.isActive('bold') ? 'bg-gray-300' : ''}`}
                            type="button"
                            title="Bold"
                          >
                            <strong>B</strong>
                          </button>
                          <button
                            onClick={() => greetingEditor?.chain().focus().toggleItalic().run()}
                            className={`p-2 rounded hover:bg-gray-200 ${greetingEditor?.isActive('italic') ? 'bg-gray-300' : ''}`}
                            type="button"
                            title="Italic"
                          >
                            <em>I</em>
                          </button>
                          <button
                            onClick={() => greetingEditor?.chain().focus().toggleUnderline().run()}
                            className={`p-2 rounded hover:bg-gray-200 ${greetingEditor?.isActive('underline') ? 'bg-gray-300' : ''}`}
                            type="button"
                            title="Underline"
                          >
                            <u>U</u>
                          </button>
                        </div>
                        <EditorContent
                          editor={greetingEditor}
                          className="prose max-w-none p-4 min-h-[80px] focus:outline-none"
                        />
                      </div>
                      <p className="text-xs text-gray-600 mt-2">
                        Beschikbare placeholders:
                        <code className="ml-2 px-2 py-0.5 bg-gray-100 rounded text-xs">{'{{first_name|lezer}}'}</code>
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Afsluiting template
                      </label>
                      <div className="border border-gray-300 rounded-lg overflow-hidden">
                        <div className="bg-gray-50 border-b border-gray-300 p-2 flex gap-1">
                          <button
                            onClick={() => closingEditor?.chain().focus().toggleBold().run()}
                            className={`p-2 rounded hover:bg-gray-200 ${closingEditor?.isActive('bold') ? 'bg-gray-300' : ''}`}
                            type="button"
                            title="Bold"
                          >
                            <strong>B</strong>
                          </button>
                          <button
                            onClick={() => closingEditor?.chain().focus().toggleItalic().run()}
                            className={`p-2 rounded hover:bg-gray-200 ${closingEditor?.isActive('italic') ? 'bg-gray-300' : ''}`}
                            type="button"
                            title="Italic"
                          >
                            <em>I</em>
                          </button>
                          <button
                            onClick={() => closingEditor?.chain().focus().toggleUnderline().run()}
                            className={`p-2 rounded hover:bg-gray-200 ${closingEditor?.isActive('underline') ? 'bg-gray-300' : ''}`}
                            type="button"
                            title="Underline"
                          >
                            <u>U</u>
                          </button>
                        </div>
                        <EditorContent
                          editor={closingEditor}
                          className="prose max-w-none p-4 min-h-[100px] focus:outline-none"
                        />
                      </div>
                      <p className="text-xs text-gray-600 mt-2">
                        Beschikbare placeholders:
                        <code className="ml-2 px-2 py-0.5 bg-gray-100 rounded text-xs">{'{{first_name}}'}</code>
                        <code className="ml-2 px-2 py-0.5 bg-gray-100 rounded text-xs">{'{{company_name}}'}</code>
                      </p>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-sm text-blue-800 mb-2">
                        <strong>Tips:</strong>
                      </p>
                      <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                        <li>Gebruik <code className="px-1 py-0.5 bg-blue-100 rounded">{'{{first_name|lezer}}'}</code> voor aanhef met fallback</li>
                        <li>De templates worden automatisch toegevoegd aan alle gegenereerde email campaigns</li>
                        <li>Je kunt ze daarna nog handmatig aanpassen per campaign</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl border border-gray-200 p-6">
                  <h2 className="text-lg font-bold text-[#231F20] mb-2 flex items-center gap-2">
                    <MapPin className="w-5 h-5" />
                    Email footer gegevens
                  </h2>
                  <p className="text-sm text-gray-600 mb-6">
                    Deze gegevens worden automatisch toegevoegd aan de footer van je email campaigns (wettelijk verplicht)
                  </p>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Adres
                      </label>
                      <input
                        type="text"
                        name="address"
                        value={profileData.address}
                        onChange={handleProfileChange}
                        placeholder="Straatnaam 123"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent outline-none"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Postcode
                        </label>
                        <input
                          type="text"
                          name="postal_code"
                          value={profileData.postal_code}
                          onChange={handleProfileChange}
                          placeholder="1234 AB"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent outline-none"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Stad
                        </label>
                        <input
                          type="text"
                          name="city"
                          value={profileData.city}
                          onChange={handleProfileChange}
                          placeholder="Amsterdam"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent outline-none"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Land
                      </label>
                      <select
                        name="country"
                        value={profileData.country}
                        onChange={(e) => setProfileData(prev => ({ ...prev, country: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent outline-none"
                      >
                        <option value="Nederland">Nederland</option>
                        <option value="België">België</option>
                        <option value="Duitsland">Duitsland</option>
                        <option value="Frankrijk">Frankrijk</option>
                        <option value="Verenigde Staten">Verenigde Staten</option>
                        <option value="Verenigd Koninkrijk">Verenigd Koninkrijk</option>
                      </select>
                    </div>

                    {(!profileData.address || !profileData.city || !profileData.postal_code) && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-amber-900 mb-1">
                            Onvolledige gegevens
                          </p>
                          <p className="text-sm text-amber-800">
                            Vul alle adresgegevens in om email campaigns te kunnen versturen volgens de wettelijke eisen.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'blog' && (
              <div className="space-y-6">
                <BlogSettingsSection
                  ref={blogSettingsRef}
                  userId={user.id}
                  onSuccess={(msg) => addToast('success', msg)}
                  onError={(msg) => addToast('error', msg)}
                />

                <div className="bg-white rounded-2xl border border-gray-200 p-6">
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <h2 className="text-lg font-bold text-[#231F20] flex items-center gap-2">
                        <Calendar className="w-5 h-5" />
                        Blog reminder planning
                      </h2>
                      <p className="text-sm text-gray-600 mt-1">
                        Ontvang automatisch herinneringen om een blog te maken
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={scheduleData.blog_schedule_enabled}
                        onChange={(e) => handleScheduleChange('blog_schedule_enabled', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#FF6B35]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF6B35]"></div>
                    </label>
                  </div>

                  {scheduleData.blog_schedule_enabled && (
                    <div className="space-y-4 pt-4 border-t border-gray-200">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                          Dagen van de week
                        </label>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                          {DAYS_OF_WEEK.map(day => (
                            <label
                              key={day.value}
                              className="flex items-center gap-2 cursor-pointer group"
                            >
                              <input
                                type="checkbox"
                                checked={scheduleData.blog_schedule_days_of_week.includes(day.value)}
                                onChange={() => handleDayToggle(day.value)}
                                className="w-4 h-4 text-[#FF6B35] border-gray-300 rounded focus:ring-[#FF6B35]"
                              />
                              <span className="text-sm text-gray-700 group-hover:text-[#FF6B35] transition-colors">
                                {day.label}
                              </span>
                            </label>
                          ))}
                        </div>
                        {scheduleData.blog_schedule_days_of_week.length === 0 && (
                          <p className="text-sm text-red-600 mt-2">
                            Selecteer minimaal één dag
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Tijd
                        </label>
                        <input
                          type="time"
                          value={scheduleData.blog_schedule_time}
                          onChange={(e) => handleScheduleChange('blog_schedule_time', e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent outline-none"
                        />
                      </div>

                      {nextScheduledDate && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <div className="flex items-start gap-3">
                            <Clock className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="text-sm font-medium text-blue-900 mb-1">
                                Volgende geplande reminder
                              </p>
                              <p className="text-sm text-blue-700">
                                {nextScheduledDate.toLocaleDateString('nl-NL', {
                                  weekday: 'long',
                                  day: 'numeric',
                                  month: 'long',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </p>
                              <p className="text-xs text-blue-600 mt-2">
                                Je ontvangt een email met een directe link om een nieuwe blog te maken.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'business_profile' && (
              <div className="space-y-6">
                <div className="bg-white rounded-2xl border border-gray-200 p-6">
                  <div className="mb-4">
                    <h2 className="text-lg font-bold text-[#231F20] flex items-center gap-2">
                      <Briefcase className="w-5 h-5" />
                      Bedrijfsprofiel
                    </h2>
                    <p className="text-sm text-gray-600 mt-1">
                      Beschrijf je bedrijf en activiteiten om de AI beter te laten aansluiten bij jouw merk
                    </p>
                  </div>

                  <div className="mt-4">
                    <textarea
                      value={businessProfile}
                      onChange={(e) => setBusinessProfile(e.target.value)}
                      placeholder="Beschrijf hier je bedrijf, je doelgroep, je producten/diensten, je tone of voice, etc. Bijvoorbeeld: 'Wij zijn een marketingbureau gespecialiseerd in B2B marketing voor tech bedrijven. Onze tone of voice is professioneel maar toegankelijk.'"
                      className="w-full min-h-[300px] max-h-[600px] px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent outline-none resize-y"
                    />
                    <p className="text-xs text-gray-600 mt-3">
                      Deze informatie wordt gebruikt door de AI om content te genereren die beter aansluit bij jouw bedrijf en merk.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'image_style' && (
              <div className="space-y-6">
                <div className="bg-white rounded-2xl border border-gray-200 p-6">
                  <div className="mb-4">
                    <h2 className="text-lg font-bold text-[#231F20] flex items-center gap-2">
                      <Image className="w-5 h-5" />
                      Afbeeldingsstijl
                    </h2>
                    <p className="text-sm text-gray-600 mt-1">
                      Geef de AI instructies over welke stijl afbeeldingen je wilt genereren
                    </p>
                  </div>

                  <div className="mt-4">
                    <textarea
                      value={imageStyle}
                      onChange={(e) => setImageStyle(e.target.value)}
                      placeholder="Beschrijf hier de gewenste stijl voor afbeeldingen. Bijvoorbeeld: 'Modern en minimalistisch, met veel wit en zachte kleuren', of 'Fotografisch met warme tinten en menselijke interactie'."
                      className="w-full min-h-[300px] max-h-[600px] px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent outline-none resize-y"
                    />
                    <p className="text-xs text-gray-600 mt-3">
                      Deze instructies worden toegevoegd aan de AI prompt bij het genereren van afbeeldingen voor je blogs.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'socials' && (
              <div className="space-y-6">
                <SocialConnectionsPanel />
              </div>
            )}

            {activeTab === 'wordpress' && (
              <div className="space-y-6">
                <KoppelingenSection />
              </div>
            )}

            {activeTab === 'subscription' && (
              <div className="space-y-6">
                {!user?.username_slug && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-amber-900">Geen gebruikersnaam ingesteld</p>
                      <p className="text-sm text-amber-800">
                        Er is nog geen gebruikersnaam voor jouw account ingesteld. Neem contact op met support om dit in te stellen.
                      </p>
                    </div>
                  </div>
                )}

                {user?.username_slug && (
                  <div className="bg-white rounded-2xl border border-gray-200 p-6">
                    <h2 className="text-lg font-bold text-[#231F20] mb-1 flex items-center gap-2">
                      <ExternalLink className="w-5 h-5" />
                      Jouw inschrijflink
                    </h2>
                    <p className="text-sm text-gray-600 mb-4">
                      Deel deze link om bezoekers te laten inschrijven voor jouw mailinglist.
                    </p>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700 font-mono truncate">
                        https://lees-nu.nl/inschrijven/{user.username_slug}
                      </div>
                      <button
                        onClick={handleCopySubscribeLink}
                        className="flex items-center gap-2 px-4 py-3 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors flex-shrink-0"
                      >
                        {copiedLink ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                        {copiedLink ? 'Gekopieerd!' : 'Kopieer'}
                      </button>
                      <a
                        href={`https://lees-nu.nl/inschrijven/${user.username_slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-4 py-3 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors flex-shrink-0"
                      >
                        <ExternalLink className="w-4 h-4" />
                        Bekijk
                      </a>
                    </div>
                  </div>
                )}

                <div className="bg-white rounded-2xl border border-gray-200 p-6">
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <h2 className="text-lg font-bold text-[#231F20] flex items-center gap-2">
                        <UserPlus className="w-5 h-5" />
                        Inschrijfformulier
                      </h2>
                      <p className="text-sm text-gray-600 mt-1">
                        Stel de teksten in voor jouw publieke inschrijfpagina
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={subscriptionForm.is_active}
                        onChange={(e) => setSubscriptionForm(prev => ({ ...prev, is_active: e.target.checked }))}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#FF6B35]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF6B35]"></div>
                    </label>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Titel</label>
                      <input
                        type="text"
                        value={subscriptionForm.title}
                        onChange={(e) => setSubscriptionForm(prev => ({ ...prev, title: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Omschrijving</label>
                      <textarea
                        value={subscriptionForm.description}
                        onChange={(e) => setSubscriptionForm(prev => ({ ...prev, description: e.target.value }))}
                        rows={3}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent outline-none resize-none"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Knoptekst</label>
                        <input
                          type="text"
                          value={subscriptionForm.button_text}
                          onChange={(e) => setSubscriptionForm(prev => ({ ...prev, button_text: e.target.value }))}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Succestittel</label>
                        <input
                          type="text"
                          value={subscriptionForm.success_title}
                          onChange={(e) => setSubscriptionForm(prev => ({ ...prev, success_title: e.target.value }))}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent outline-none"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Succesbericht</label>
                      <input
                        type="text"
                        value={subscriptionForm.success_message}
                        onChange={(e) => setSubscriptionForm(prev => ({ ...prev, success_message: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent outline-none"
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl border border-gray-200 p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h2 className="text-lg font-bold text-[#231F20] flex items-center gap-2">
                        <Mail className="w-5 h-5" />
                        Dubbele opt-in
                      </h2>
                      <p className="text-sm text-gray-600 mt-1">
                        Stuur een bevestigingsemail voordat iemand aan de lijst wordt toegevoegd
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={subscriptionForm.confirmation_required}
                        onChange={(e) => setSubscriptionForm(prev => ({ ...prev, confirmation_required: e.target.checked }))}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#FF6B35]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF6B35]"></div>
                    </label>
                  </div>

                  {subscriptionForm.confirmation_required && (
                    <div className="space-y-4 pt-4 border-t border-gray-200">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Onderwerp bevestigingsemail</label>
                        <input
                          type="text"
                          value={subscriptionForm.confirmation_subject}
                          onChange={(e) => setSubscriptionForm(prev => ({ ...prev, confirmation_subject: e.target.value }))}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Bericht in bevestigingsemail</label>
                        <textarea
                          value={subscriptionForm.confirmation_message}
                          onChange={(e) => setSubscriptionForm(prev => ({ ...prev, confirmation_message: e.target.value }))}
                          rows={3}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent outline-none resize-none"
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="bg-white rounded-2xl border border-gray-200 p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h2 className="text-lg font-bold text-[#231F20] flex items-center gap-2">
                        <Shield className="w-5 h-5" />
                        AVG / GDPR toestemming
                      </h2>
                      <p className="text-sm text-gray-600 mt-1">
                        Toon een verplichte toestemmingscheckbox op het inschrijfformulier
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={subscriptionForm.gdpr_enabled}
                        onChange={(e) => setSubscriptionForm(prev => ({ ...prev, gdpr_enabled: e.target.checked }))}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#FF6B35]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF6B35]"></div>
                    </label>
                  </div>

                  {subscriptionForm.gdpr_enabled && (
                    <div className="space-y-4 pt-4 border-t border-gray-200">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Toestemmingstekst</label>
                        <textarea
                          value={subscriptionForm.gdpr_text}
                          onChange={(e) => setSubscriptionForm(prev => ({ ...prev, gdpr_text: e.target.value }))}
                          rows={3}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent outline-none resize-none"
                          placeholder="Ik ga akkoord met de verwerking van mijn gegevens voor het ontvangen van de nieuwsbrief."
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          De bezoeker moet dit vakje aanvinken voordat ze zich kunnen inschrijven.
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          URL privacyverklaring <span className="text-gray-400 font-normal">(optioneel)</span>
                        </label>
                        <input
                          type="url"
                          value={subscriptionForm.gdpr_privacy_url}
                          onChange={(e) => setSubscriptionForm(prev => ({ ...prev, gdpr_privacy_url: e.target.value }))}
                          placeholder="https://jouwwebsite.nl/privacy"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent outline-none"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Als je dit invult, wordt er een klikbare link "Lees onze privacyverklaring" toegevoegd.
                        </p>
                      </div>
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <p className="text-xs text-blue-800">
                          <strong>Tip:</strong> Onder de AVG ben je verplicht om toestemming te vragen voor e-mailmarketing. Een expliciete checkbox (niet vooraf aangevinkt) is de aanbevolen methode.
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-end gap-4 pb-32">
                  <button
                    onClick={handleSaveSubscriptionForm}
                    disabled={savingSubscription}
                    className="btn-primary flex items-center gap-2"
                  >
                    {savingSubscription ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Opslaan...
                      </>
                    ) : (
                      <>
                        <Save className="w-5 h-5" />
                        Opslaan
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'billing' && (
              <div className="space-y-6">
                <div className="bg-white rounded-2xl border border-gray-200 p-6">
                  <h2 className="text-lg font-bold text-[#231F20] mb-2 flex items-center gap-2">
                    <Receipt className="w-5 h-5" />
                    Facturen & abonnement
                  </h2>
                  <p className="text-sm text-gray-600 mb-6">
                    Je abonnement, facturen en betaalmethode beheer je in het Plug&Pay klantenportaal.
                    Daar kun je je facturen (PDF en UBL) downloaden, je betaalcyclus aanpassen en je
                    betaalgegevens wijzigen.
                  </p>

                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 mb-6">
                    <h3 className="text-sm font-semibold text-[#231F20] mb-3">Wat kun je in het klantenportaal?</h3>
                    <ul className="text-sm text-gray-700 space-y-2">
                      <li className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-[#FF6B35] flex-shrink-0 mt-0.5" />
                        Facturen downloaden (PDF + UBL)
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-[#FF6B35] flex-shrink-0 mt-0.5" />
                        Factuurgegevens en e-mailadres aanpassen
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-[#FF6B35] flex-shrink-0 mt-0.5" />
                        Betaalmethode toevoegen of wijzigen
                      </li>
                    </ul>
                  </div>

                  <a
                    href={PLUGANDPAY_PORTAL_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary inline-flex items-center gap-2"
                  >
                    <ExternalLink className="w-5 h-5" />
                    Open klantenportaal
                  </a>

                  <p className="text-xs text-gray-500 mt-4">
                    Je logt in via een magische link: vul je e-mailadres in en je ontvangt direct een
                    inlog-link in je inbox. Het portaal opent in een nieuw tabblad.
                  </p>
                </div>

                <div className="bg-white rounded-2xl border border-gray-200 p-6">
                  <h2 className="text-lg font-bold text-[#231F20] mb-2 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5" />
                    Abonnement opzeggen
                  </h2>

                  {!cancellationLoaded ? (
                    <div className="py-4 text-sm text-gray-500">Laden...</div>
                  ) : activeCancellation ? (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
                      <p className="text-sm text-amber-900">
                        <strong>Je abonnement is opgezegd.</strong> Je toegang loopt nog door tot{' '}
                        <strong>{formatDateNL(activeCancellation.effective_end_date)}</strong>. Daarna stopt
                        je toegang tot dibbi automatisch.
                      </p>
                      <p className="text-sm text-amber-900 mt-3">
                        Wil je je opzegging ongedaan maken? Mail{' '}
                        <a href="mailto:admin@dibbi.nl" className="underline font-medium">admin@dibbi.nl</a>.
                      </p>
                    </div>
                  ) : (
                    <>
                      <p className="text-sm text-gray-600 mb-4">
                        Bij opzegging blijft je account actief tot{' '}
                        <strong>{formatDateNL(calculateEffectiveEndDate(user.created_at))}</strong> —
                        daarna stopt je toegang automatisch.
                      </p>
                      <button
                        onClick={() => setCancelModalOpen(true)}
                        className="px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 font-medium text-sm"
                      >
                        Abonnement opzeggen
                      </button>
                    </>
                  )}
                </div>

                <CancelSubscriptionModal
                  open={cancelModalOpen}
                  onClose={() => setCancelModalOpen(false)}
                  onSuccess={(endDate) => {
                    setCancelModalOpen(false);
                    setActiveCancellation({
                      id: 'just-created',
                      cancelled_at: new Date().toISOString(),
                      effective_end_date: endDate,
                    });
                    addToast('success', 'Je opzegging is verwerkt. Je ontvangt een bevestiging per e-mail.');
                  }}
                  userCreatedAt={user.created_at}
                  effectiveEndDatePreview={calculateEffectiveEndDate(user.created_at)}
                />
              </div>
            )}

            {activeTab !== 'subscription' && activeTab !== 'billing' && activeTab !== 'socials' && activeTab !== 'wordpress' && (
              <div className="flex items-center justify-end gap-4 pb-32">
                <button
                  onClick={() => navigate('/dashboard')}
                  className="btn-secondary"
                >
                  Annuleren
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="btn-primary flex items-center gap-2"
                >
                  {saving ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Opslaan...
                    </>
                  ) : (
                    <>
                      <Save className="w-5 h-5" />
                      Opslaan
                    </>
                  )}
                </button>
              </div>
            )}
              </div>
            </div>
          </div>
        </main>
      </div>
      <ToastContainer toasts={toasts} onClose={removeToast} />
    </div>
  );
}
